<template>
    <nav class="mobile-bottom-bar" aria-label="Primary navigation">
        <button
            type="button"
            class="mobile-bottom-bar-button mobile-bottom-bar-menu"
            :class="{
                'mobile-bottom-bar-button--preparing': preparing,
                'mobile-bottom-bar-button--returning': returning
            }"
            aria-controls="sideNavBarCanvas"
            aria-label="Open menu"
            :aria-describedby="chatUnread ? 'mobile-chat-unread' : undefined"
            @click="openMenu"
            @animationend="returning = false"
        >
            <i class="fas fa-bars" aria-hidden="true"></i>
            <span v-if="chatUnread" class="chat-badge mobile-chat-badge" aria-hidden="true">{{
                chatUnread > 99 ? "99+" : chatUnread
            }}</span>
            <span v-if="chatUnread" id="mobile-chat-unread" class="visually-hidden">
                {{ chatUnread }} unread chat
                {{ chatUnread === 1 ? "conversation" : "conversations" }}
            </span>
        </button>
    </nav>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import { Offcanvas } from "bootstrap";
import { hapticMenuOpened } from "@/services/HapticsService";
import { mobileNavigation } from "@/services/sidebarFocus";

defineProps({ chatUnread: { type: Number, default: 0 } });

const preparing = ref(false);
const returning = ref(false);
function openMenu(event) {
    hapticMenuOpened();
    // The data API unconditionally restores launcher focus after hide, even
    // when a drawer action has already handed focus to search or another dialog.
    const drawer = mobileNavigation();
    if (drawer) Offcanvas.getOrCreateInstance(drawer).show(event.currentTarget);
}

function prepareReturn(event) {
    if (event.target.id !== "sideNavBarCanvas") return;
    returning.value = false;
    preparing.value = true;
}

function playReturn(event) {
    if (event.target.id !== "sideNavBarCanvas") return;
    preparing.value = false;
    returning.value = true;
}

onMounted(() => {
    document.addEventListener("hide.bs.offcanvas", prepareReturn);
    document.addEventListener("hidden.bs.offcanvas", playReturn);
});

onBeforeUnmount(() => {
    document.removeEventListener("hide.bs.offcanvas", prepareReturn);
    document.removeEventListener("hidden.bs.offcanvas", playReturn);
});
</script>

<style scoped>
.mobile-chat-badge {
    position: absolute;
    top: -0.15rem;
    right: -0.25rem;
    box-shadow: 0 0 0 2px var(--forkto-app-ground);
}
.mobile-bottom-bar-menu {
    position: relative;
}
.mobile-bottom-bar {
    position: fixed;
    z-index: 999;
    right: auto;
    bottom: var(--forkto-mobile-nav-optical-bottom, 0.875rem);
    left: 0;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0 max(var(--forkto-mobile-nav-edge-gap, 0.875rem), var(--forkto-safe-area-right, 0px))
        0 max(var(--forkto-mobile-nav-edge-gap, 0.875rem), var(--forkto-safe-area-left, 0px));
    pointer-events: none;
}

.mobile-bottom-bar-button {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: var(--forkto-mobile-nav-control-size, 3rem);
    height: var(--forkto-mobile-nav-control-size, 3rem);
    padding: 0;
    color: rgba(255, 255, 255, 0.86);
    text-decoration: none;
    background: rgba(14, 14, 14, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 50%;
    font-size: 1.125rem;
    box-shadow:
        0 0.35rem 1.1rem rgba(0, 0, 0, 0.22),
        inset 0 1px 0 rgba(255, 255, 255, 0.08);
    -webkit-backdrop-filter: blur(18px) saturate(130%);
    backdrop-filter: blur(18px) saturate(130%);
    pointer-events: auto;
    transition:
        transform 0.16s ease,
        color 0.16s ease,
        background-color 0.16s ease,
        box-shadow 0.16s ease;
}

.mobile-bottom-bar-button:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
}

.mobile-bottom-bar-button--returning {
    transform-origin: bottom left;
    animation: mobile-menu-return 240ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.mobile-bottom-bar-button--preparing {
    opacity: 0;
    transform: translate(-0.3rem, 0.3rem) scale(0.72);
    transition: none;
}

@keyframes mobile-menu-return {
    0% {
        opacity: 0;
        transform: translate(-0.3rem, 0.3rem) scale(0.72);
        box-shadow:
            0 0.1rem 0.35rem rgba(0, 0, 0, 0.12),
            inset 0 1px 0 rgba(255, 255, 255, 0.04);
    }
    68% {
        opacity: 1;
        transform: translate(0, 0) scale(1.04, 0.98);
        box-shadow:
            0 0.45rem 1.25rem rgba(0, 0, 0, 0.24),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
    }
    100% {
        opacity: 1;
        transform: translate(0, 0) scale(1);
    }
}

.mobile-bottom-bar-button:active {
    transform: scale(0.94);
}

@media (prefers-reduced-motion: reduce) {
    .mobile-bottom-bar-button--returning {
        animation: none;
    }

    .mobile-bottom-bar-button:active {
        transform: none;
    }
}
</style>
